export const placementSideValues = ['top', 'left', 'bottom', 'right'] as const;
export type PlacementSide = (typeof placementSideValues)[number];

export const alignmentValues = ['start', 'center', 'end'] as const;
export type PopoverAlignment = (typeof alignmentValues)[number];

export interface PopoverPlacement {
    side: PlacementSide;
    align: PopoverAlignment;
}

const MIN_SWITCH_DELTA = 4;

const oppositeSide: Record<PlacementSide, PlacementSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
};

function placementKey(c: PopoverPlacement): string {
    return `${c.side}-${c.align}`;
}

function dedupePlacements(chain: PopoverPlacement[]): PopoverPlacement[] {
    const seen = new Set<string>();
    const out: PopoverPlacement[] = [];
    for (const c of chain) {
        const k = placementKey(c);
        if (!seen.has(k)) {
            seen.add(k);
            out.push(c);
        }
    }
    return out;
}

/**
 * Ordered position-try list rooted at preferred side/align (then opposites / orthogonals).
 * Used after the current placement stops being fully valid; see `resolvePopoverPlacement`.
 */
export function buildPlacementCandidateChain(
    side: PlacementSide,
    align: PopoverAlignment
): PopoverPlacement[] {
    const opposite = oppositeSide[side];
    if (align === 'center') {
        const isVertical = side === 'top' || side === 'bottom';
        return [
            { side, align },
            { side: opposite, align },
            { side: isVertical ? 'right' : 'bottom', align },
            { side: isVertical ? 'left' : 'top', align },
        ];
    }
    const flipped: PopoverAlignment = align === 'start' ? 'end' : 'start';
    const inline: PopoverPlacement[] = [
        { side, align },
        { side: opposite, align },
        { side, align: flipped },
        { side: opposite, align: flipped },
    ];
    if (side === 'left' || side === 'right') {
        return [...inline, { side: 'bottom', align: 'center' }, { side: 'top', align: 'center' }];
    }
    return [...inline, { side: 'right', align: 'center' }, { side: 'left', align: 'center' }];
}

/** Offset of the popover's top-left corner relative to the anchor's top-left corner. */
export function computePopoverOffset(
    side: PlacementSide,
    align: PopoverAlignment,
    anchorWidth: number,
    anchorHeight: number,
    popoverWidth: number,
    popoverHeight: number,
    gap: number
): { top: number; left: number } {
    if (side === 'top' || side === 'bottom') {
        const top = side === 'bottom' ? anchorHeight + gap : -popoverHeight - gap;
        let left: number;
        if (align === 'start') left = 0;
        else if (align === 'end') left = anchorWidth - popoverWidth;
        else left = (anchorWidth - popoverWidth) / 2;
        return { top, left };
    }
    const left = side === 'right' ? anchorWidth + gap : -popoverWidth - gap;
    let top: number;
    if (align === 'start') top = 0;
    else if (align === 'end') top = anchorHeight - popoverHeight;
    else top = (anchorHeight - popoverHeight) / 2;
    return { top, left };
}

function viewportRectForPopover(
    anchorRelativeOffset: { top: number; left: number },
    anchorRect: DOMRect,
    popoverWidth: number,
    popoverHeight: number
): { left: number; top: number; right: number; bottom: number } {
    const left = anchorRect.left + anchorRelativeOffset.left;
    const top = anchorRect.top + anchorRelativeOffset.top;
    return { left, top, right: left + popoverWidth, bottom: top + popoverHeight };
}

function popoverIntersectsAnchor(
    anchorRelativeOffset: { top: number; left: number },
    anchorRect: DOMRect,
    popoverWidth: number,
    popoverHeight: number
): boolean {
    const p = viewportRectForPopover(anchorRelativeOffset, anchorRect, popoverWidth, popoverHeight);
    return !(
        p.right <= anchorRect.left ||
        p.left >= anchorRect.right ||
        p.bottom <= anchorRect.top ||
        p.top >= anchorRect.bottom
    );
}

function overflowPastViewport(
    anchorRelativeOffset: { top: number; left: number },
    anchorRect: DOMRect,
    popoverWidth: number,
    popoverHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    pad: number
): number {
    const p = viewportRectForPopover(anchorRelativeOffset, anchorRect, popoverWidth, popoverHeight);
    return (
        Math.max(0, pad - p.left) +
        Math.max(0, pad - p.top) +
        Math.max(0, p.right - (viewportWidth - pad)) +
        Math.max(0, p.bottom - (viewportHeight - pad))
    );
}

function scorePlacement(
    c: PopoverPlacement,
    offsetFor: (p: PopoverPlacement) => { top: number; left: number },
    anchorRect: DOMRect,
    popoverWidth: number,
    popoverHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    viewportPad: number
): { overflow: number; overlaps: boolean } {
    const offset = offsetFor(c);
    return {
        overflow: overflowPastViewport(
            offset,
            anchorRect,
            popoverWidth,
            popoverHeight,
            viewportWidth,
            viewportHeight,
            viewportPad
        ),
        overlaps: popoverIntersectsAnchor(offset, anchorRect, popoverWidth, popoverHeight),
    };
}

function placementsEqual(a: PopoverPlacement, b: PopoverPlacement): boolean {
    return a.side === b.side && a.align === b.align;
}

function isFullyValidPlacementScore(s: { overflow: number; overlaps: boolean }): boolean {
    return s.overflow === 0 && !s.overlaps;
}

export function resolvePopoverPlacement(
    previous: PopoverPlacement | null,
    preferredSide: PlacementSide,
    preferredAlign: PopoverAlignment,
    anchorRect: DOMRect,
    popoverWidth: number,
    popoverHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    gap: number,
    viewportPad: number
): PopoverPlacement {
    const { width: aw, height: ah } = anchorRect;
    const candidates = dedupePlacements(
        buildPlacementCandidateChain(preferredSide, preferredAlign)
    );

    const offsetFor = (c: PopoverPlacement): { top: number; left: number } =>
        computePopoverOffset(c.side, c.align, aw, ah, popoverWidth, popoverHeight, gap);

    const score = (c: PopoverPlacement): { overflow: number; overlaps: boolean } =>
        scorePlacement(
            c,
            offsetFor,
            anchorRect,
            popoverWidth,
            popoverHeight,
            viewportWidth,
            viewportHeight,
            viewportPad
        );

    // Align with CSS anchor position / position-try stability: while the last placement still fits
    // (no overflow, no anchor overlap), keep it — do not jump back to an earlier/higher-priority try.
    if (
        previous &&
        candidates.some(c => placementsEqual(c, previous)) &&
        isFullyValidPlacementScore(score(previous))
    ) {
        return previous;
    }

    for (const candidate of candidates) {
        const s = score(candidate);
        if (isFullyValidPlacementScore(s)) {
            return candidate;
        }
    }

    let best = candidates[0];
    let bestScore = score(best);

    for (const candidate of candidates) {
        const s = score(candidate);
        const isBetter =
            (!s.overlaps && bestScore.overlaps) ||
            (s.overlaps === bestScore.overlaps &&
                s.overflow + MIN_SWITCH_DELTA < bestScore.overflow);
        if (isBetter) {
            best = candidate;
            bestScore = s;
        }
    }

    if (previous && candidates.some(c => placementsEqual(c, previous))) {
        const prevScore = score(previous);
        const bestIsStrictlyBetterThanPrevious =
            (!bestScore.overlaps && prevScore.overlaps) ||
            (bestScore.overlaps === prevScore.overlaps &&
                bestScore.overflow + MIN_SWITCH_DELTA < prevScore.overflow);
        if (!bestIsStrictlyBetterThanPrevious) {
            return previous;
        }
    }

    return best;
}
